import { useRouter } from 'next/router';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { Reducer, useImmerReducer } from 'use-immer';
import { sanitiseUrl } from '../libs/utils';

export type Settings = {};

export type AnalyticsStateContextType = Settings & {
  isLoading: boolean;
  logEvent({ action, params }): void;
};

export type AnalyticsDispatchContextType = {
  setSettings: (settings: Settings) => void;
};

type AnalyticsContextProps = {
  children: React.ReactNode;
};

export const AnalyticsStateContext = createContext({} as AnalyticsStateContextType);
export const AnalyticsDispatchContext = createContext({} as AnalyticsDispatchContextType);

const eventReducer: Reducer<AnalyticsStateContextType, any> = (draft, action) => {
  switch (action.type) {
    case 'setSettings': {
      draft.isLoading = false;
      break;
    }
    default: {
      throw new Error(`Unhandled dispatch: ${action.type}`);
    }
  }
};

const initialState = {};

export function AnalyticsProvider({ children }: AnalyticsContextProps): any {
  const [state, dispatch] = useImmerReducer<any, any>(eventReducer, initialState);
  const router = useRouter();
  const [trackingCode] = useState(process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS);

  const setSettings = (newSettings: Settings) => {
    dispatch({ type: 'setSettings', settings: newSettings });
  };

  // log the pageview with their URL
  const pageview = (url) => {
    const sanitisedUrlObj = sanitiseUrl(url);
    // @ts-ignore
    window.gtag('config', trackingCode, {
      page_title: sanitisedUrlObj.pageTitle,
      page_path: sanitisedUrlObj.pagePath
    });
  };

  useEffect(() => {
    const handleRouteChange = (asPath) => {
      pageview(asPath);
    };
    // When the component is mounted, subscribe to router changes
    // and log those page views
    router.events.on('routeChangeComplete', handleRouteChange);
    router.events.on('hashChangeComplete', handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
      router.events.off('hashChangeComplete', handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    pageview(window.location.href);
  }, []);

  // log specific events happening.
  const logEvent = async ({ action, params }) => {
    // console.log('############ logging event!!! ############');
    // console.log({ action, params });
    // @ts-ignore
    window.gtag('event', action, params);
  };

  // log specific events happening.
  const signUpEvent = async ({ email }) => {
    // console.log('############ signup event!!! ############');
    // @ts-ignore
    window.gtag('event', 'sign_up', { method: btoa(email) });
  };

  const dispatchContextValue = useMemo(() => ({ setSettings }), [setSettings]);

  return (
    <AnalyticsDispatchContext.Provider value={dispatchContextValue}>
      <AnalyticsStateContext.Provider value={{ ...state, logEvent, signUpEvent }}>
        {children}
      </AnalyticsStateContext.Provider>
    </AnalyticsDispatchContext.Provider>
  );
}
